import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ChevronRight, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import EmptyState from "@/components/EmptyState";
import { fmtMoneyList } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function initials(name) {
  const parts = (name || "?").trim().split(/\s+/);
  return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
}

export default function People() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const { data: people = [], isLoading } = useQuery({
    queryKey: ["people"],
    queryFn: async () => (await api.get("/people")).data,
  });

  const filtered = people.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="pb-4">
      <h1 className="font-display text-2xl font-bold text-foreground">People</h1>
      <div className="mt-4 relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          data-testid="people-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search people"
          className="h-12 pl-11 rounded-xl bg-white"
        />
      </div>

      {isLoading ? (
        <div className="mt-4 space-y-2.5">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState testId="people-empty" icon={Users} title="No people yet" subtitle="People you lend to will show up here once you record a debt." />
      ) : (
        <div className="mt-4 space-y-2.5">
          {filtered.map((p) => (
            <button
              key={p.id}
              data-testid={`person-row-${p.id}`}
              onClick={() => navigate(`/people/${p.id}`)}
              className="w-full text-left bg-card rounded-2xl border border-border p-4 flex items-center gap-3 active:scale-[0.99] transition-transform hover:border-primary/30"
            >
              <div className="h-11 w-11 shrink-0 rounded-full bg-secondary text-primary font-display font-bold flex items-center justify-center">
                {initials(p.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground truncate">{p.name}</p>
                {(p.owed_to_me_by_currency?.length > 0 || p.i_owe_by_currency?.length > 0) ? (
                  <div className="text-sm space-y-0.5">
                    {p.owed_to_me_by_currency?.length > 0 && (
                      <p className="text-muted-foreground">Owes you {fmtMoneyList(p.owed_to_me_by_currency)}</p>
                    )}
                    {p.i_owe_by_currency?.length > 0 && (
                      <p className="text-foreground/70">You owe {fmtMoneyList(p.i_owe_by_currency)}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-emerald-700 font-medium">All settled ✓</p>
                )}
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
